import { useEffect, useState } from 'react'
import Select, { components } from 'react-select'
import theme from '../../../presentation/styles/theme'

import * as S from './styles'

type SelectInputProps = {
  label?: string
  labelColor?: string
  hasTestSelectInput?: boolean
  hasConclusionInput?: boolean
  hasQuestionInput?: boolean
  hasSelectOption?: boolean
  hasAvaibleDefault?: boolean
  name: string
  options: {
    value: any
    label: string
  }[]
  onChange(event: any): void
  wrapperClassName?: string
  value?: any
  disabled?: boolean
  loading?: boolean
  isSearchable?: boolean
  placeholder?: string
}

const CustomOption = ({ innerProps, ...otherProps }: any) => {
  delete otherProps.isFocused
  delete innerProps.onMouseOver
  delete innerProps.onMouseMove

  const newProps = { innerProps: { ...innerProps }, ...otherProps }
  return (
    <components.Option {...newProps}>{otherProps.children}</components.Option>
  )
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <S.Icon />
    </components.DropdownIndicator>
  )
}

const MAX_VALUE_TO_SHOW = 400

const SelectInput = ({
  label,
  labelColor,
  hasTestSelectInput = false,
  hasConclusionInput = false,
  hasQuestionInput = false,
  name,
  options,
  onChange,
  wrapperClassName,
  value,
  disabled,
  loading,
  ...props
}: SelectInputProps) => {
  const [slicedOptions, setSlicedOptions] = useState<any[]>([])

  useEffect(() => {
    if (options.length > MAX_VALUE_TO_SHOW) {
      setSlicedOptions(options.slice(0, MAX_VALUE_TO_SHOW))
    } else {
      setSlicedOptions(options || [])
    }
  }, [options])

  function escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
  }

  const filter = (value: string): void => {
    if (!value) {
      setSlicedOptions(options.slice(0, MAX_VALUE_TO_SHOW))
    }

    const matchByStart = []
    const matchByInclusion = []

    const regByInclusion = new RegExp(escapeRegExp(value), 'i')
    const regByStart = new RegExp(`^${escapeRegExp(value)}`, 'i')

    for (const option of options) {
      if (regByInclusion.test(option.label)) {
        if (regByStart.test(option.label)) {
          matchByStart.push(option)
        } else {
          matchByInclusion.push(option)
        }
      }
    }

    const temp = [...matchByStart, ...matchByInclusion]

    if (temp.length > MAX_VALUE_TO_SHOW) {
      setSlicedOptions(temp.slice(0, MAX_VALUE_TO_SHOW))
    } else {
      setSlicedOptions(temp)
    }
  }

  if (options && options.length > MAX_VALUE_TO_SHOW) {
    return (
      <S.Wrapper
        className={wrapperClassName}
        hasTestSelectInput={hasTestSelectInput}
        hasConclusionInput={hasConclusionInput}
        hasQuestionInput={hasQuestionInput}
      >
        <label style={{ color: labelColor }} htmlFor={name}>
          {label}
        </label>

        <Select
          styles={{
            control: (base, { isDisabled }) => ({
              ...base,
              borderRadius: 10,
              borderWidth: 0.5,
              paddingTop: 6,
              paddingBottom: 6,
              border: `1px solid ${
                isDisabled ? '#cecece' : theme.colors.lightGray
              }`,
              background: theme.colors.white,
              ':hover': {
                outline: 'none',
                borderColor: theme.colors.secondary,
                boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.1)'
              }
            }),
            menu: (base) => ({ ...base, borderRadius: 8 }),
            placeholder: (base) => ({
              ...base,
              color: '#acacac'
            }),
            option: (base, { isSelected }) => ({
              ...base,
              backgroundColor: !isSelected
                ? base.backgroundColor
                : theme.colors.primary,
              ':hover': {
                backgroundColor: `${theme.colors.primary}33`
              }
            }),
            indicatorSeparator: (base) => ({
              ...base,
              display: 'none'
            }),
            dropdownIndicator: (base, { isDisabled }) => ({
              ...base,
              'margin-right': 10,
              opacity: isDisabled ? 0.3 : 1
            })
          }}
          options={slicedOptions}
          placeholder=""
          noOptionsMessage={() => <>Sem opções</>}
          components={{ DropdownIndicator, Option: CustomOption }}
          onChange={(e) => onChange(e?.value)}
          value={value}
          filterOption={() => true}
          onInputChange={filter}
          isDisabled={disabled}
          isLoading={loading}
          loadingMessage={() => <>Carregando...</>}
          {...props}
        />
      </S.Wrapper>
    )
  }

  return (
    <S.Wrapper
      className={wrapperClassName}
      hasTestSelectInput={hasTestSelectInput}
      hasConclusionInput={hasConclusionInput}
      hasQuestionInput={hasQuestionInput}
    >
      <label style={{ color: labelColor }} htmlFor={name}>
        {label}
      </label>

      <Select
        styles={{
          control: (base, { isDisabled }) => ({
            ...base,
            borderRadius: 10,
            borderWidth: 0.5,
            paddingTop: 6,
            paddingBottom: 6,
            border: `1px solid ${
              isDisabled ? '#cecece' : theme.colors.lightGray
            }`,
            background: theme.colors.white,
            ':hover': {
              outline: 'none',
              borderColor: theme.colors.secondary,
              boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.1)'
            }
          }),
          menu: (base) => ({ ...base, borderRadius: 8 }),
          placeholder: (base) => ({
            ...base,
            color: '#acacac'
          }),
          option: (base, { isSelected }) => ({
            ...base,
            backgroundColor: !isSelected
              ? base.backgroundColor
              : theme.colors.primary,
            ':hover': {
              backgroundColor: `${theme.colors.primary}33`
            }
          }),
          indicatorSeparator: (base) => ({
            ...base,
            display: 'none'
          }),
          dropdownIndicator: (base, { isDisabled }) => ({
            ...base,
            marginRight: 10,
            opacity: isDisabled ? 0.3 : 1
          })
        }}
        options={options}
        placeholder=""
        noOptionsMessage={() => <>Sem opções</>}
        components={{ DropdownIndicator, Option: CustomOption }}
        onChange={(e) => onChange(e?.value)}
        value={value}
        isDisabled={disabled}
        isLoading={loading}
        loadingMessage={() => <>Carregando...</>}
        {...props}
      />
    </S.Wrapper>
  )
}

export default SelectInput
